import { ColumnConfig, numberSort, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX, useEffect, useMemo, useState } from "react";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { getComparisonChangeElement } from "@app/shared/utility/getComparisonChangeElement";
import { Version } from "@app/members/memberSetModels";
import MemberSetService from "@app/members/memberSetService";

interface MemberSetCountByStateProps {
  selectedVersion: Version;
  comparedVersion: Version;
  compareMode: boolean;
}

interface MemberSetCountByStateTableData {
  [wafflesKeyColumnName]: string;
  comparedMemberCount?: string;
  comparedNumericMemberCount?: number;
  change?: JSX.Element;
  changeNumeric?: number;
  changePercent?: JSX.Element;
  changePercentNumeric?: number;
  memberCount?: string;
  numericMemberCount?: number;
  stateAbbreviation: string;
}

const MemberSetCountByStateTable = (props: MemberSetCountByStateProps): JSX.Element => {
  const compareMode = props.compareMode;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<JSX.Element>(null);
  const [stateMemberCountModels, setStateMemberCountModels] = useState<MemberSetCountByStateTableData[]>([]);
  const [stateComparedMemberCountModels, setStateComparedMemberCountModels] = useState<MemberSetCountByStateTableData[]>([]);
  const memberSetService = new MemberSetService();

  const getColumns = useMemo(() => {
    const compared = props.compareMode;
    const memberSetCountByCountyColumnConfig: ColumnConfig = {
      change: {
        displayText: "Change",
        hidden: !compared,
        testId: "change",
      },
      changePercent: {
        displayText: "% Change",
        hidden: !compared,
        testId: "change-percent",
      },
      comparedMemberCount: {
        displayText: props.compareMode && props.comparedVersion ? new Date(props.comparedVersion.dateCreated).toLocaleString("en-US") : "",
        hidden: !compared,
        testId: "compared-count",
      },
      memberCount: {
        displayText: compared ? new Date(props.selectedVersion.dateCreated).toLocaleString("en-US") : "Count",
        testId: "count",
      },
      stateAbbreviation: {
        displayText: "State",
        testId: "state",
      },
    };

    return memberSetCountByCountyColumnConfig;
  }, [props.compareMode, props.comparedVersion, props.selectedVersion]);

  const getData = useMemo(() => {
    /* oxlint-disable sort-keys */
    const data = compareMode && stateComparedMemberCountModels ? stateComparedMemberCountModels.map((value) => {
      const location = stateMemberCountModels.findIndex((obj) => {
        return obj.stateAbbreviation === value.stateAbbreviation;
      });
      //if it is in both objects, comparing is pretty straight forward, the order matters here
      if (location !== -1) {
        const difference = stateMemberCountModels[location].numericMemberCount - value.comparedNumericMemberCount;

        return {
          [wafflesKeyColumnName]: stateMemberCountModels[location][wafflesKeyColumnName],
          stateAbbreviation: stateMemberCountModels[location].stateAbbreviation,
          comparedMemberCount: value.comparedMemberCount,
          comparedNumericCompareMemberCount: value.comparedNumericMemberCount,
          memberCount: stateMemberCountModels[location].memberCount,
          numericMemberCount: stateMemberCountModels[location].numericMemberCount,
          change: getComparisonChangeElement(difference),
          changeNumeric: difference,
          changePercent: getComparisonChangeElement(((difference / value.comparedNumericMemberCount) * 100), (num: number) => num.toFixed(1)),
          changePercentNumeric: ((difference / value.comparedNumericMemberCount) * 100),
        };
      } else {
        return {
          ...value,
          memberCount: "0",
          numericMemberCount: 0,
          change: getComparisonChangeElement(0 - value.comparedNumericMemberCount, (num: number) => num.toFixed(1)),
          changeNumeric: 0 - value.comparedNumericMemberCount,
          changePercent: getComparisonChangeElement(-100),
          changePercentNumeric: -100,
        };
      }
    }) : stateMemberCountModels;
    /* oxlint-enable sort-keys */
    return data;
  }, [compareMode, stateComparedMemberCountModels, stateMemberCountModels]);

  useEffect(() => {
    setLoading(true);
    /* oxlint-disable sort-keys */
    memberSetService.getMemberCountByState(props.selectedVersion.memberSetId, props.selectedVersion.memberSetVersionId).then((result) => {
      setStateMemberCountModels(result.map((stateMemberCountModel) => {
        return {
          [wafflesKeyColumnName]: stateMemberCountModel.stateAbbreviation,
          stateAbbreviation: stateMemberCountModel.stateAbbreviation,
          memberCount: stateMemberCountModel.memberCount.toLocaleString(),
          numericMemberCount: stateMemberCountModel.memberCount,
        };

      }));
      if (!props.comparedVersion) {
        setLoading(false);
      }
    }, () => {
      setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
    });

    props.comparedVersion ?
      memberSetService.getMemberCountByState(props.comparedVersion.memberSetId, props.comparedVersion.memberSetVersionId).then((result) => {
        setStateComparedMemberCountModels(result.map((stateMemberCountModel) => {
          return {
            [wafflesKeyColumnName]: stateMemberCountModel.stateAbbreviation,
            stateAbbreviation: stateMemberCountModel.stateAbbreviation,
            comparedMemberCount: stateMemberCountModel.memberCount.toLocaleString(),
            comparedNumericMemberCount: stateMemberCountModel.memberCount,
          };

        }));
        setLoading(false);
      }, () => {
        setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
      }) : setStateComparedMemberCountModels(null);

    /* oxlint-enable sort-keys */
  }, [props.comparedVersion, props.selectedVersion]);

  if (error) {
    return error;
  }

  return (
    <Waffles<MemberSetCountByStateTableData>
      columns={getColumns}
      customSort={customSort}
      data={getData}
      loading={loading}
      fullWidth={true}
      testId="member-count-by-state"
    />
  );
};

const customSort = (records: MemberSetCountByStateTableData[], column: keyof MemberSetCountByStateTableData, sortDirection: SortDirections) => {
  let newRecords = [...records];
  const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

  switch (column) {
    case "stateAbbreviation":
      newRecords = newRecords.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      break;
    case "memberCount":
      newRecords = numberSort(newRecords, "numericMemberCount", multiplier);
      break;
    case "comparedMemberCount":
      newRecords = numberSort(newRecords, "comparedNumericMemberCount", multiplier);
      break;
    case "change":
      newRecords = numberSort(newRecords, "changeNumeric", multiplier);
      break;
    case "changePercent":
      newRecords = numberSort(newRecords, "changePercentNumeric", multiplier);
      break;
    default:
      break;
  }

  return newRecords;
};

export default MemberSetCountByStateTable;
